import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { Box, Button, CircularProgress, Container, Tab, Typography } from '@mui/material';
import Stack from '@mui/material/Stack';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import * as CalculationHelper from '../api/calculation/calculationHelper';
import { IGetCalculateAsync } from '../api/calculation/iApiRequest';
import * as DrainPipeHelper from '../api/drainPipe/drainPipeHelper';
import { EStatusCode } from '../api/enum';
import * as ProjectHelper from '../api/project/projectHelper';
import Span from '../shared/component/Span';
import { EEngineType, EResultCalculationType } from '../shared/enum';
import InputDischarge from '../shared/input/discharge/InputDischarge';
import InputDrainageTrench from '../shared/input/drainageTrench/InputDrainageTrench';
import InputLoad from '../shared/input/InputLoad';
import InputSlope from '../shared/input/InputSlope';
import InputSoil from '../shared/input/soil/InputSoil';
import { IProject } from '../shared/iProject';
import variable from '../shared/style/variable';
import { dialog } from '../shared/util';
import { selectAlert } from '../store/alert/alertSlice';
import { selectDrainPipe, setDrainPipe } from '../store/drainPipe/drainPipeSlice';
import { EReduxState } from '../store/enum';
import { useAppDispatch, useAppSelector } from '../store/hook';
import { setMapImage } from '../store/Image/imageSlice';
import { selectProject, setPhaseOneOutputCalculation, setProject } from '../store/project/projectSlice';
import { selectResult, setResult } from '../store/result/resultSlice';
import { selectUser } from '../store/user/userSlice';
import Engine from './engine/Index';
import PreliminaryCalculation from './engine/PreliminaryCalculation';
import ResultCalculation from './engine/ResultCalculation';
import Result from './result/Index';

interface IDesignProps {}

const DesignContainer = styled(Container)`
    padding: 0;
    flex-grow: 1;
`;

var map: any;

const Design = (props: IDesignProps) => {
    let { projectId } = useParams();
    const [searchParams] = useSearchParams();

    const [indexTab, setIndexTab] = React.useState('1');

    const { t } = useTranslation();

    let leaflet = (window as any).L;
    let leafletImage = (window as any).leafletImage;

    const divMap = React.useRef(null);

    const dispatch = useAppDispatch();
    const project = useAppSelector(selectProject);
    const drainPipe = useAppSelector(selectDrainPipe);
    const result = useAppSelector(selectResult);
    const user = useAppSelector(selectUser);
    const alert = useAppSelector(selectAlert);

    const [isLoading, setIsLoading] = React.useState(false);

    const handleChangeTab = (event: React.SyntheticEvent, newValue: string) => {
        setIndexTab(newValue);
    };

    const hasSecondaryDrainageTrench = project.data?.drainageTrenches?.length! > 1;

    React.useEffect(() => {
        const handleFetchProject = async () => {
            setIsLoading(true);
            let project = await ProjectHelper.getProjectAsync(parseInt(projectId!), user.data!.email);
            dispatch(setProject(project.data));
            if (drainPipe.status == EReduxState.initialState) {
                let resDrainPipe = await DrainPipeHelper.getDrainPipeForCountryAsync(project.data.projectInfo.projectCountryCode!);
                dispatch(setDrainPipe(resDrainPipe.data));
            }
            setIsLoading(false);
            if (!!searchParams.get('withCalculation')) handleRunCalculation(project.data);
        };
        if (project.status == EReduxState.initialState) {
            handleFetchProject();
        }
    }, [projectId]);

    React.useEffect(() => {
        if (indexTab == '5') {
            setIndexTab('1');
        }
        if (!hasSecondaryDrainageTrench && indexTab == '4') {
            setIndexTab('1');
        }
    }, [JSON.stringify(project.data)]);

    React.useEffect(() => {
        if (result.status == EReduxState.fulfilled) {
            var latitude = project.data?.projectInfo?.latitude!;
            var longitude = project.data?.projectInfo?.longitude!;
            if (map) map.remove();
            map = leaflet.map(divMap.current!).setView([latitude, longitude], 8);
            leaflet.marker([latitude, longitude]).addTo(map);
            leaflet
                .tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
                    maxZoom: 19,
                    attribution: '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>',
                })
                .addTo(map);
            //leafletImage works only map is in DOM, display none not work
            leafletImage(map, (err: any, canvas: any) => {
                dispatch(setMapImage(canvas.toDataURL()));
            });
        }
    }, [result]);

    const handleRunCalculation = async (project: IProject) => {
        setIsLoading(true);
        let payload: IGetCalculateAsync = {
            project,
        };
        try {
            let res = await CalculationHelper.getCalculateAsync(payload);
            if (res?.status == EStatusCode.Ok) {
                dispatch(setPhaseOneOutputCalculation(res!.data.phaseOneOutputCalculation));
                dispatch(setResult(res!.data));
                setIndexTab('5');
            }
        } catch (error) {
            dialog('warning', t('oopsCalculationError'));
        }
        setIsLoading(false);
    };

    return (
        <DesignContainer maxWidth={false}>
            {result.status == EReduxState.fulfilled && (
                <>
                    <Stack ref={divMap} style={{ width: 300, height: 300, position: 'absolute', zIndex: '-1' }} />
                    <Stack style={{ width: 300, height: 427, position: 'absolute', zIndex: '-1' }}>
                        <PreliminaryCalculation withExport />
                    </Stack>
                    <Stack style={{ width: 434, height: 300, position: 'absolute', visibility: 'hidden' }}>
                        <Engine type={EEngineType.GeneralView} withExport />
                    </Stack>
                    <Stack style={{ width: 600, height: 300, position: 'absolute', visibility: 'hidden' }}>
                        <Engine type={EEngineType.TransversalSectionPrimaryDrainageTrench} withExport />
                    </Stack>
                    {hasSecondaryDrainageTrench && (
                        <Stack style={{ width: 600, height: 300, position: 'absolute', visibility: 'hidden' }}>
                            <Engine type={EEngineType.TransversalSectionSecondaryDrainageTrench} withExport />
                        </Stack>
                    )}
                    <Stack style={{ width: 434, height: 300, position: 'absolute', zIndex: '-1' }}>
                        <Engine type={EEngineType.LongitudinalStaticSection} withExport />
                    </Stack>
                    <Stack style={{ width: 434, height: 300, position: 'absolute', zIndex: '-1' }}>
                        <Engine type={EEngineType.LongitudinalSeismicSection} withExport />
                    </Stack>
                    <Stack style={{ width: 434, height: 300, position: 'absolute', zIndex: '-1' }}>
                        <ResultCalculation type={EResultCalculationType.Axis} withExport />
                    </Stack>
                    <Stack style={{ width: 434, height: 300, position: 'absolute', zIndex: '-1' }}>
                        <ResultCalculation type={EResultCalculationType.PreFsStatic} withExport />
                    </Stack>
                    <Stack style={{ width: 434, height: 300, position: 'absolute', zIndex: '-1' }}>
                        <ResultCalculation type={EResultCalculationType.PostFsStatic} withExport />
                    </Stack>
                    <Stack style={{ width: 434, height: 300, position: 'absolute', zIndex: '-1' }}>
                        <ResultCalculation type={EResultCalculationType.PreFsSeismic} withExport />
                    </Stack>
                    <Stack style={{ width: 434, height: 300, position: 'absolute', zIndex: '-1' }}>
                        <ResultCalculation type={EResultCalculationType.PostFsSeismic} withExport />
                    </Stack>
                </>
            )}
            <Stack height={'100%'} direction={'row'} spacing={0.5}>
                {isLoading && <CircularProgress />}
                {!isLoading && project.status == EReduxState.fulfilled && (
                    <>
                        <Stack sx={{ width: { xs: '30%', md: '25%' }, backgroundColor: variable.grey_100 }} position='relative'>
                            <Stack variant='scrollable'>
                                <Typography style={{ padding: '5px 16px', fontSize: '0.8rem', color: variable.white, backgroundColor: variable.green }}>{t('drainDesign')}</Typography>
                                <InputDrainageTrench />
                                <InputDischarge />
                                <Typography style={{ padding: '5px 16px', fontSize: '0.8rem', color: variable.white, backgroundColor: variable.green }}>{t('slopeAndLoad')}</Typography>
                                <InputSlope />
                                <InputSoil />
                                <InputLoad />
                            </Stack>
                        </Stack>
                        <Stack sx={{ width: { xs: '70%', md: '75%' } }}>
                            <TabContext value={indexTab}>
                                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                                    <Stack direction={'row'} justifyContent={'space-between'} padding={'0 1rem'}>
                                        <Stack direction={'row'} sx={{ width: { xs: '55%', sm: '70%', md: '80%', lg: 'auto' } }}>
                                            <TabList variant='scrollable' onChange={handleChangeTab} aria-label='Engine tab'>
                                                <Tab variant='standard' label={t('preliminaryCalculation')} value='1' />
                                                {result.status == EReduxState.fulfilled && <Tab variant='standard' label={t('3DEngine')} value='2' />}
                                                <Tab
                                                    variant='standard'
                                                    label={
                                                        <Stack>
                                                            <Stack>{t('slopeTransversalEngine')}</Stack>
                                                            <Stack>{t('primaryDrain')}</Stack>
                                                        </Stack>
                                                    }
                                                    value='3'
                                                />
                                                {hasSecondaryDrainageTrench && (
                                                    <Tab
                                                        variant='standard'
                                                        label={
                                                            <Stack>
                                                                <Stack>{t('slopeTransversalEngine')}</Stack>
                                                                <Stack>{t('secondaryDrain')}</Stack>
                                                            </Stack>
                                                        }
                                                        value='4'
                                                    />
                                                )}
                                                <Tab variant='standard' label={t('slopeLongitudinalEngine')} value='5' />
                                            </TabList>
                                        </Stack>
                                        <Stack direction={'row'} justifyContent={'end'}>
                                            {result.status == EReduxState.fulfilled && (
                                                <TabList onChange={handleChangeTab} aria-label='Result tab'>
                                                    <Tab variant={`${!alert.data?.title ? 'standard' : 'warning'}`} label={t('results')} value='6' />
                                                </TabList>
                                            )}
                                            <Button variant='green' startIcon={<PlayArrowIcon color='orange' fontSize='small' />} onClick={() => handleRunCalculation(project.data!)}>
                                                <Span text={t('run')} />
                                            </Button>
                                        </Stack>
                                    </Stack>
                                </Box>
                                <TabPanel value='1' style={{ height: '100%', padding: 0 }}>
                                    <PreliminaryCalculation />
                                </TabPanel>
                                {result.status == EReduxState.fulfilled && (
                                    <TabPanel value='2' style={{ height: '100%', padding: 0 }}>
                                        <Engine type={EEngineType.GeneralView} />
                                    </TabPanel>
                                )}
                                <TabPanel value='3' style={{ height: '100%', padding: 0 }}>
                                    <Engine type={EEngineType.TransversalSectionPrimaryDrainageTrench} />
                                </TabPanel>
                                {hasSecondaryDrainageTrench && (
                                    <TabPanel value='4' style={{ height: '100%', padding: 0 }}>
                                        <Engine type={EEngineType.TransversalSectionSecondaryDrainageTrench} />
                                    </TabPanel>
                                )}
                                <TabPanel value='5' style={{ height: '100%', padding: 0 }}>
                                    <Engine type={EEngineType.LongitudinalSection} />
                                </TabPanel>
                                {result.status == EReduxState.fulfilled && (
                                    <TabPanel value='6' style={{ height: '90%', padding: '10px' }}>
                                        <Stack height={'100%'} position={'relative'}>
                                            <Result />
                                        </Stack>
                                    </TabPanel>
                                )}
                            </TabContext>
                        </Stack>
                    </>
                )}
            </Stack>
        </DesignContainer>
    );
};

export default Design;
